import {
  Accordion,
  Checkbox,
  FileDropField,
  FlexSpacer,
  Icon,
  Paragraph,
  Spinner,
  Stack,
  Text,
  Title,
  Link,
  Tooltip,
  Wizard,
  useWizardPage
} from '@efast_public/fjd-component-library';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { Trans, useTranslation } from 'react-i18next';
import {
  ClientDestinationsModel,
  Destination,
  DestinationSources,
  isDecryptionKey,
  isSigningKey,
  OrganizationalUnit,
  UploadedKey
} from '../../../../api/hooks/useDestinations';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../../../shared/notification/useNotification';
import { WeiterleitungsWizardState } from '../../../../modals/ReceiverSettingsWizard/Wizard.types';
import { SyncResultDto } from '../../../../api/hooks/useSyncResult';
import { fileToString } from '../../../../shared/utils/fileUtils';
import KeyConfirmationModal from '../../../../modals/KeyConfirmationModal/KeyConfirmationModal';
import { BehApiRoutes, behClient } from '../../../../api/beh-api';
import {
  conflictCasesLinked,
  conflictMultipleMandantsLinked,
  conflictMultipleUsedDestination,
  fitconnectEncryptionKeyNotFound,
  fitconnectEncryptionKeypairInvalid,
  fitconnectSigningKeyNotFound,
  fitconnectSigningKeypairInvalid,
  ErrorType,
  fitconnectDestinationNotFound
} from '../../../../shared/utils/Error/ErrorCode';
import {
  FJD_DOCS_EINRICHTUNGSASSISTANT,
  FITCONNECT_DOCS_KEYS,
  FJD_DOCS_SETTINGS_FITCONNECT
} from '../../../../shared/utils/Links';
import type {
  IconAppearance,
  IconGlyph
} from '@efast_public/fjd-component-library/build/src/components/Icon/Icon.types';

interface ConfirmationModel {
  destinationId: string;
  key: UploadedKey;
}

interface FitconnectPublicKeyResponse {
  kid: string;
  key_ops: string[];
}

type KeyHandleResult = 'ERROR' | 'SUCCESS' | 'ADDED_TO_CONFIRMATION';

const successIconProperties = {
  name: 'checkmark-filled' as IconGlyph,
  variant: 'success' as IconAppearance
};

const defaultIconProperties = {
  name: 'circle-dash' as IconGlyph
};

const errorIconProperties = {
  name: 'close-filled' as IconGlyph,
  variant: 'error' as IconAppearance
};

function DestinationKeyUpload({
  isSettings
}: {
  isSettings?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUser, notifyUserFromRequest } = useNotification();
  const [toConfirm, setToConfirm] = useState<Array<ConfirmationModel>>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [shouldUseOneKeyForAll, setShouldUseOneKeyForAll] =
    useState<boolean>(false);

  const [orgUnits, setOrgUnits] = useState<ClientDestinationsModel>();
  const [invalidDecryptionDestinationIds, setInvalidDecryptionDestinationIds] =
    useState<Set<string>>(new Set());
  const [invalidSigningDestinationIds, setInvalidSigningDestinationIds] =
    useState<Set<string>>(new Set());
  const [validatingDestinationId, setValidatingDestinationId] = useState<
    string | null
  >(null);

  const { mutateAsync: postDestination } = useMutation({
    mutationFn: ({
      payload,
      source
    }: {
      payload?: ClientDestinationsModel;
      source?: DestinationSources;
    }) =>
      notifyUserFromRequest(
        () =>
          behClient.post<SyncResultDto>(
            BehApiRoutes.destinations(source),
            payload,
            { timeout: 120000 }
          ),
        {
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_CASES_LINKED]: () => {
              notifyUser({
                intent: 'error',
                title: conflictCasesLinked
              });
            },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_CASES_MULTIPLE_USED_DESTINATIONS_LINKED]:
              () => {
                notifyUser({
                  intent: 'error',
                  title: conflictMultipleUsedDestination
                });
              },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_MULTIPLE_MANDANTS_LINKED]:
              () => {
                notifyUser({
                  intent: 'error',
                  title: conflictMultipleMandantsLinked
                });
              },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_FITCONNECT_DESTINATION_NOT_FOUND]:
              () => {
                notifyUser({
                  intent: 'error',
                  title: fitconnectDestinationNotFound
                });
              },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_ENCRYPTION_KEY_NOT_FOUND]:
              () => {
                notifyUser({
                  intent: 'error',
                  title: fitconnectEncryptionKeyNotFound
                });
              },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_SIGNING_KEY_NOT_FOUND]: () => {
              notifyUser({
                intent: 'error',
                title: fitconnectSigningKeyNotFound
              });
            },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_JWK_ENCRYPTION_KEYPAIR_VALIDATION_ERROR]:
              () => {
                notifyUser({
                  intent: 'error',
                  title: fitconnectEncryptionKeypairInvalid
                });
              },
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_JWK_SIGNING_KEYPAIR_VALIDATION_ERROR]:
              () => {
                notifyUser({
                  intent: 'error',
                  title: fitconnectSigningKeypairInvalid
                });
              }
          }
        }
      ),
    onSuccess: (syncResult, variables) => {
      setWizardState((prevState) => ({
        ...prevState,
        state: {
          ...(prevState.state as WeiterleitungsWizardState),
          syncResult: syncResult
        }
      }));
      queryClient.invalidateQueries({
        queryKey: [BehApiRoutes.destinations(variables.source)]
      });
      queryClient.invalidateQueries({
        predicate: (key) => {
          return key.queryKey.some((keyElement) => {
            if (typeof keyElement === 'string') {
              if (keyElement.startsWith(BehApiRoutes.orgUnits())) {
                return true;
              }
            }
            return false;
          });
        }
      });
      queryClient.invalidateQueries({
        predicate: (key) => {
          return key.queryKey.some((keyElement) => {
            if (typeof keyElement === 'string') {
              if (keyElement.startsWith(BehApiRoutes.configurationBaseUri())) {
                return true;
              }
            }
            return false;
          });
        }
      });
    },
    onError: () => {
      setOrgUnits((prevState) => ({
        organizationalUnitDtos: prevState!.organizationalUnitDtos.map(
          (orgUnit) => ({
            ...orgUnit,
            destinationDtos: orgUnit.destinationDtos.map((destination) => ({
              ...destination,
              privateDecryptionKey: null,
              privateSigningKey: null
            }))
          })
        )
      }));
    }
  });
  const areAllKeysAvailable = useMemo(
    () =>
      orgUnits?.organizationalUnitDtos?.every(
        ({ destinationDtos }) =>
          destinationDtos[0]?.privateDecryptionKey != null &&
          destinationDtos[0]?.privateSigningKey != null
      ) ?? false,
    [orgUnits]
  );

  const onSubmit = useCallback(
    () =>
      (orgUnits?.organizationalUnitDtos?.length ?? 0) === 0
        ? Promise.resolve(undefined)
        : postDestination({
            payload: orgUnits,
            source: DestinationSources.newFitconnect
          }),
    [orgUnits, postDestination]
  );
  const { wizardState, setWizardState } = useWizardPage({
    isFormValid: areAllKeysAvailable,
    headline: (state) =>
      `Schritt ${state.currentPageIndex + 1} von ${state.pagesLength}`,
    onSubmit
  });

  useEffect(() => {
    const orgUnits = (
      wizardState.state as WeiterleitungsWizardState | undefined
    )?.orgUnits;
    if (orgUnits) {
      setOrgUnits(orgUnits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirmation = () => {
    if (toConfirm.length <= 0) {
      setModalOpen(false);
      return;
    }

    let result: Array<KeyHandleResult> = [];

    toConfirm.forEach((confirmObj: ConfirmationModel) => {
      setOrgUnits((prevState: ClientDestinationsModel | undefined) => {
        if (!prevState) {
          return prevState;
        }
        const newState: ClientDestinationsModel = {
          ...prevState,
          organizationalUnitDtos: prevState.organizationalUnitDtos.map(
            (orgUnit: OrganizationalUnit) => {
              return {
                ...orgUnit,
                destinationDtos: orgUnit.destinationDtos.map(
                  (value: Destination) => {
                    if (value.destinationId !== confirmObj.destinationId) {
                      return value;
                    }

                    if (isDecryptionKey(confirmObj.key)) {
                      result.push('SUCCESS');
                      return { ...value, privateDecryptionKey: confirmObj.key };
                    } else if (isSigningKey(confirmObj.key)) {
                      result.push('SUCCESS');
                      return { ...value, privateSigningKey: confirmObj.key };
                    } else {
                      result.push('ERROR');
                      return value;
                    }
                  }
                )
              };
            }
          )
        };
        return newState;
      });
    });
    setToConfirm([]);
    setModalOpen(false);

    if (result.includes('SUCCESS')) {
      notifyUser({
        title: t(
          'receiverSettingsWizard.destinationKeyUpload.keyUpload.success'
        ),
        intent: 'error'
      });
    } else {
      notifyUser({
        title: t('receiverSettingsWizard.destinationKeyUpload.keyUpload.error'),
        intent: 'error'
      });
    }
  };

  const onDecline = (): void => {
    setToConfirm([]);
  };

  const clearKeyFromOrgUnits = (
    destination: Destination,
    isDecryption: boolean
  ): void => {
    setOrgUnits((prevState) => {
      if (!prevState) return prevState;
      return {
        ...prevState,
        organizationalUnitDtos: prevState.organizationalUnitDtos.map(
          (orgUnit) => ({
            ...orgUnit,
            destinationDtos: orgUnit.destinationDtos.map((value) => {
              if (value.destinationId !== destination.destinationId)
                return value;
              return isDecryption
                ? { ...value, privateDecryptionKey: null }
                : { ...value, privateSigningKey: null };
            })
          })
        )
      };
    });
  };

  const handleKey = (
    key: UploadedKey,
    destination: Destination | undefined = undefined
  ): Array<KeyHandleResult> => {
    const result: Array<KeyHandleResult> = [];
    let hasDecryptionError = false;
    let hasSigningError = false;
    let hasDecryptionSuccess = false;
    let hasSigningSuccess = false;
    const appliesToDestination = (value: Destination) =>
      destination == null || destination.destinationId === value.destinationId;

    setOrgUnits((prevState: ClientDestinationsModel | undefined) => {
      if (!prevState) {
        return prevState;
      }

      const newState = {
        ...prevState,
        organizationalUnitDtos: prevState.organizationalUnitDtos.map(
          (orgUnit) => {
            return {
              ...orgUnit,
              destinationDtos: orgUnit.destinationDtos.map((value) => {
                if (!appliesToDestination(value)) {
                  return value;
                }
                if (isDecryptionKey(key)) {
                  console.log('log', key, value);
                  // if (
                  //   value.encryptionKid !== null &&
                  //   value.encryptionKid !== key.kid
                  // ) {
                  //   console.log("log a", key)
                  //   hasDecryptionError = true;
                  //   result.push('ERROR');
                  //   return value;
                  // }
                  if (
                    value.privateDecryptionKey !== null &&
                    value.privateDecryptionKey.kid !== key.kid
                  ) {
                    console.log('log e', key);

                    setToConfirm((toConfirmValues) => {
                      return [
                        ...toConfirmValues,
                        { key: key, destinationId: value.destinationId }
                      ];
                    });
                    result.push('ADDED_TO_CONFIRMATION');
                    return value;
                  } else {
                    console.log('log s', key);
                    result.push('SUCCESS');
                    hasDecryptionSuccess = true;
                    return { ...value, privateDecryptionKey: key };
                  }
                } else if (isSigningKey(key)) {
                  if (
                    value.privateSigningKey !== null &&
                    value.privateSigningKey.kid !== key.kid
                  ) {
                    setToConfirm((toConfirmValues) => {
                      return [
                        ...toConfirmValues,
                        { key: key, destinationId: value.destinationId }
                      ];
                    });
                    result.push('ADDED_TO_CONFIRMATION');
                    return value;
                  } else {
                    result.push('SUCCESS');
                    hasSigningSuccess = true;
                    return { ...value, privateSigningKey: key };
                  }
                } else {
                  notifyUser({
                    title: t(
                      'receiverSettingsWizard.destinationKeyUpload.keyUpload.error'
                    ),
                    intent: 'error'
                  });
                  return value;
                }
              })
            };
          }
        )
      };
      console.log('log', newState);
      return newState;
    });

    if (isDecryptionKey(key)) {
      if (hasDecryptionError) {
        if (destination) {
          setInvalidDecryptionDestinationIds((prevState) => {
            const nextState = new Set(prevState);
            nextState.add(destination.destinationId);
            return nextState;
          });
        }
      } else if (hasDecryptionSuccess) {
        if (destination) {
          setInvalidDecryptionDestinationIds((prevState) => {
            const nextState = new Set(prevState);
            nextState.delete(destination.destinationId);
            return nextState;
          });
        }
      }
    }

    if (isSigningKey(key)) {
      if (hasSigningError) {
        if (destination) {
          setInvalidSigningDestinationIds((prevState) => {
            const nextState = new Set(prevState);
            nextState.add(destination.destinationId);
            return nextState;
          });
        }
      } else if (hasSigningSuccess) {
        if (destination) {
          setInvalidSigningDestinationIds((prevState) => {
            const nextState = new Set(prevState);
            nextState.delete(destination.destinationId);
            return nextState;
          });
        }
      }
    }

    return result;
  };

  function handleKeyUpload(
    files: Array<File>,
    destination: Destination | undefined = undefined
  ) {
    files.map((file: File) => readFileOnUpload(file, destination));
  }

  const validateAndApplyKeyToDestination = async (
    key: UploadedKey,
    destination: Destination
  ): Promise<Array<KeyHandleResult>> => {
    if (key.kid && (isDecryptionKey(key) || isSigningKey(key))) {
      const isDecryption = isDecryptionKey(key);
      let publicKeyDto: FitconnectPublicKeyResponse | null = null;

      try {
        setValidatingDestinationId(destination.destinationId);
        const response = await queryClient.fetchQuery({
          queryKey: [
            BehApiRoutes.fitconnectPublicKey(destination.destinationId, key.kid)
          ],
          queryFn: () =>
            behClient.get<FitconnectPublicKeyResponse>(
              BehApiRoutes.fitconnectPublicKey(
                destination.destinationId,
                key.kid
              )
            ),
          staleTime: 5 * 60 * 1000
        });
        publicKeyDto = response.data;
      } catch (err: unknown) {
        setValidatingDestinationId(null);
        const status =
          err != null &&
          typeof err === 'object' &&
          'response' in err &&
          err.response != null &&
          typeof err.response === 'object' &&
          'status' in err.response
            ? (err.response as { status: number }).status
            : undefined;
        if (status === 404) {
          clearKeyFromOrgUnits(destination, isDecryption);
          isDecryption
            ? setInvalidDecryptionDestinationIds((prevState) => {
                const nextState = new Set(prevState);
                nextState.add(destination.destinationId);
                return nextState;
              })
            : setInvalidSigningDestinationIds((prevState) => {
                const nextState = new Set(prevState);
                nextState.add(destination.destinationId);
                return nextState;
              });
        }
        return ['ERROR'];
      }

      setValidatingDestinationId(null);
      const expectedKeyOp = isDecryption ? 'wrapKey' : 'verify';
      if (!publicKeyDto.key_ops.includes(expectedKeyOp)) {
        clearKeyFromOrgUnits(destination, isDecryption);
        isDecryption
          ? setInvalidDecryptionDestinationIds((prevState) => {
              const nextState = new Set(prevState);
              nextState.add(destination.destinationId);
              return nextState;
            })
          : setInvalidSigningDestinationIds((prevState) => {
              const nextState = new Set(prevState);
              nextState.add(destination.destinationId);
              return nextState;
            });
        return ['ERROR'];
      }
    }

    return handleKey(key, destination);
  };

  const readFileOnUpload = (
    uploadedFile: File,
    destination: Destination | undefined = undefined
  ) => {
    fileToString(uploadedFile)
      .then(async (fileAsString) => {
        const key = JSON.parse(fileAsString) as UploadedKey;

        const resultHandleKey = destination
          ? await validateAndApplyKeyToDestination(key, destination)
          : (
              await Promise.all(
                (orgUnits?.organizationalUnitDtos ?? []).flatMap((orgUnit) =>
                  orgUnit.destinationDtos.map((currentDestination) =>
                    validateAndApplyKeyToDestination(key, currentDestination)
                  )
                )
              )
            ).flat();

        if (resultHandleKey.includes('SUCCESS')) {
          notifyUser({
            title: t(
              'receiverSettingsWizard.destinationKeyUpload.keyUpload.success'
            ),
            intent: 'success'
          });
        } else if (resultHandleKey.includes('ERROR')) {
          const isDecryption = isDecryptionKey(key);
          notifyUser({
            title:
              destination &&
              key.kid &&
              (isDecryptionKey(key) || isSigningKey(key))
                ? isDecryption
                  ? fitconnectEncryptionKeypairInvalid
                  : fitconnectSigningKeypairInvalid
                : t(
                    'receiverSettingsWizard.destinationKeyUpload.keyUpload.error'
                  ),
            intent: 'error'
          });
        }
        setValidatingDestinationId(null);
      })
      .catch(() => {
        notifyUser({
          title: t(
            'receiverSettingsWizard.destinationKeyUpload.keyUpload.error'
          ),
          intent: 'error'
        });
      });
  };

  return (
    <Wizard.Page>
      <Stack spacing="m">
        <Title level={2} visualLevel={6}>
          {t('configAssistent.destinationKeyUpload.headline', {
            amount: orgUnits?.organizationalUnitDtos.length
          })}
        </Title>
        <Paragraph>
          <Trans
            i18nKey="configAssistent.destinationKeyUpload.descriptionText"
            components={{
              Link: (
                <Link href={FITCONNECT_DOCS_KEYS} target="_blank">
                  link
                </Link>
              )
            }}
          />
          <Trans
            i18nKey="configAssistent.configAssistentDocumentationNotice"
            components={{
              Link: (
                <Link
                  href={
                    isSettings
                      ? FJD_DOCS_SETTINGS_FITCONNECT
                      : FJD_DOCS_EINRICHTUNGSASSISTANT
                  }
                  target="_blank"
                >
                  hier
                </Link>
              )
            }}
          />
        </Paragraph>
        <Title level={2} visualLevel={6}>
          {t('Zustellpunkte')}
        </Title>
        <Stack spacing="s">
          <Checkbox
            data-testid="checkBoxUploadAll"
            defaultChecked={shouldUseOneKeyForAll}
            onChange={(event) => {
              setShouldUseOneKeyForAll(event.currentTarget.checked);
            }}
            id={'singleKeySet'}
            label="Ein Set Private Keys für alle Zustellpunkte verwenden"
          />
          {shouldUseOneKeyForAll && (
            <div>
              <FileDropField
                className="uploadField"
                data-testid="uploadFieldAll"
                label="Neue Dateien auswählen"
                dropHelperText={'oder in dieses Feld ziehen'}
                onAdd={(files) => {
                  handleKeyUpload(files);
                }}
              />
            </div>
          )}
          <Accordion.Group>
            {orgUnits && orgUnits.organizationalUnitDtos
              ? orgUnits.organizationalUnitDtos.map((orgUnit) => {
                  const destinationId =
                    orgUnit.destinationDtos[0]?.destinationId;
                  const hasDecryptionKey: boolean =
                    orgUnit.destinationDtos.filter(
                      (destination) => destination.privateDecryptionKey !== null
                    ).length > 0;

                  const hasSigningKey: boolean =
                    orgUnit.destinationDtos.filter(
                      (destination) => destination.privateSigningKey !== null
                    ).length > 0;

                  const allKeysUploaded: boolean =
                    hasDecryptionKey && hasSigningKey;
                  const hasValidationError =
                    destinationId != null &&
                    (invalidDecryptionDestinationIds.has(destinationId) ||
                      invalidSigningDestinationIds.has(destinationId));

                  return (
                    <Accordion
                      key={orgUnit.name}
                      data-testid={`accordion${orgUnit.name}`}
                      before={
                        <div>
                          <Tooltip tooltip={orgUnit.name}>
                            <Stack spacing="s" direction="row">
                              {allKeysUploaded ? (
                                <Icon
                                  size="lg"
                                  name="checkmark-filled"
                                  variant="success"
                                />
                              ) : hasValidationError ? (
                                <Icon
                                  size="lg"
                                  name="close-filled"
                                  variant="error"
                                />
                              ) : (
                                <Icon size="lg" name="circle-dash" />
                              )}
                              <span>
                                <Text strong>{orgUnit.name}</Text>
                              </span>
                            </Stack>
                          </Tooltip>
                        </div>
                      }
                    >
                      <Stack spacing="l">
                        <FlexSpacer />
                        <div>
                          {
                            orgUnit.destinationDtos[0]?.publicServiceTypeDto
                              ?.length
                          }{' '}
                          Verwaltungsleistungen
                        </div>
                        <Stack
                          style={{
                            justifyContent: 'space-between',
                            margin: '0 var(--fjd-spacing-xs)'
                          }}
                          spacing="s"
                          direction="row"
                          justifyContent="space-betweeen"
                        >
                          <Stack spacing="xs" direction={'column'}>
                            <Stack spacing="s" direction={'row'}>
                              <Icon {...successIconProperties} />
                              <span>PublicEncryption</span>
                            </Stack>
                            <Stack spacing="s" direction={'row'}>
                              <Icon
                                {...(hasDecryptionKey
                                  ? { ...successIconProperties }
                                  : destinationId != null &&
                                      invalidDecryptionDestinationIds.has(
                                        destinationId
                                      )
                                    ? { ...errorIconProperties }
                                    : { ...defaultIconProperties })}
                              />
                              <span>PrivateDecryption</span>
                            </Stack>
                          </Stack>
                          <Stack spacing="xs" direction="column">
                            <Stack spacing="s" direction={'row'}>
                              <Icon {...successIconProperties} />
                              <span>PublicSignature</span>
                            </Stack>
                            <Stack spacing="s" direction={'row'}>
                              <Icon
                                {...(hasSigningKey
                                  ? { ...successIconProperties }
                                  : destinationId != null &&
                                      invalidSigningDestinationIds.has(
                                        destinationId
                                      )
                                    ? { ...errorIconProperties }
                                    : { ...defaultIconProperties })}
                              />
                              <span>PrivateSigning</span>
                            </Stack>
                          </Stack>
                        </Stack>
                        {validatingDestinationId ===
                        orgUnit.destinationDtos[0]?.destinationId ? (
                          <Stack direction="row" justifyContent="center">
                            <Spinner />
                          </Stack>
                        ) : (
                          <FileDropField
                            data-testid={`upload${orgUnit.name}`}
                            className="uploadField"
                            label="Neue Dateien auswählen"
                            dropHelperText="oder in dieses Feld ziehen"
                            onAdd={(files: Array<File>) => {
                              if (orgUnit.destinationDtos.length <= 0) {
                                return;
                              }

                              handleKeyUpload(
                                files,
                                orgUnit.destinationDtos[0]
                              );
                            }}
                          />
                        )}
                      </Stack>
                    </Accordion>
                  );
                })
              : null}
          </Accordion.Group>
          <KeyConfirmationModal
            onConfirmation={onConfirmation}
            onClosed={onDecline}
            isOpen={modalOpen}
          />
        </Stack>
      </Stack>
    </Wizard.Page>
  );
}

export default DestinationKeyUpload;
